import styles from './DragAndDropFile.module.scss';
import React, { useState, DragEvent, ChangeEvent, useRef } from 'react';
import cn from 'classnames';
import SVGWrapper from '@repo/icons/SVGWrapper';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';

function formatBytes(a: number, b = 2): string {
  if (!+a) return '0 Bytes';
  const c = 0 > b ? 0 : b,
    d = Math.floor(Math.log(a) / Math.log(1024));
  return `${parseFloat((a / Math.pow(1024, d)).toFixed(c))} ${['Bytes', 'KB', 'MB', 'GB'][d]}`;
}

interface DragDropFileOpenerProps {
  className?: string;
  text?: string | JSX.Element;
  accept?: string;
  onFile: (file: File) => void;
}

const AddFileIcon = () => (
  <SVGWrapper viewBox="0 0 24 24" width="24" height="24" color="textPrimary">
    <circle cx="12" cy="12" r="9" fill="none" strokeWidth="2" />
    <path
      d="M12 17v-9.35l-3.5 3.5 3.5-3.5 3.5 3.5"
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SVGWrapper>
);

export const FileLoadedIcon = () => (
  <SVGWrapper viewBox="0 0 24 24" width="24" height="24" color="textPrimary">
    <defs>
      <linearGradient id="fileLoadedIconLG" x1="50%" x2="50%" y1="0%" y2="100%">
        <stop offset="0%" stopColor="#44C47F" />
        <stop offset="100%" stopColor="#34A264" />
      </linearGradient>
    </defs>
    <path
      fill="none"
      strokeWidth="2"
      stroke="url(#fileLoadedIconLG)"
      strokeLinecap="round"
      d="M8 3h5.28a2 2 0 0 1 1.28.46l4.72 3.94A2 2 0 0 1 20 8.94V17a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4ZM8 16.5h8M8 12h4M13 4.5V7c0 1.1.9 2 2 2h4"
    />
  </SVGWrapper>
);

export default function DragDropFileOpener({
  onFile,
  text = (
    <>
      Click to select a CSV file to upload
      <br />
      or drag and drop it here.
    </>
  ),
  accept = 'text/csv',
  className,
}: DragDropFileOpenerProps) {
  const [draggingOver, setDraggingOver] = useState(false);
  const [file, setFile] = useState<File>();
  const browseInputRef = useRef<HTMLInputElement | null>(null);

  const fileName = file ? file.name : null;
  const fileNameParts = fileName?.split('.');
  const fileExtension = fileNameParts?.[fileNameParts.length - 1];
  const fileNameNoExtension = fileNameParts?.slice(0, -1).join('.');

  function onDrop(event: DragEvent) {
    event.preventDefault();
    if (!event.dataTransfer?.items) {
      return;
    }

    const [item] = [...event.dataTransfer.items].filter((itm) => itm.kind === 'file');
    if (!item) {
      return;
    }
    const asFile = item.getAsFile();
    if (!asFile || !asFile.type.startsWith(accept)) {
      return;
    }
    setFile(asFile);
    onFile(asFile);
  }

  function onDragOver(event: DragEvent) {
    if (!draggingOver && event.dataTransfer?.items?.[0]?.type?.startsWith(accept)) {
      setDraggingOver(true);
    }
    event.preventDefault();
  }

  function onDragLeave() {
    if (draggingOver) {
      setDraggingOver(false);
    }
  }

  function onUploadClick() {
    browseInputRef.current?.click();
  }

  function onBrowseFileChange(e: ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || !files.length) {
      return;
    }
    const [file] = files;
    setFile(file);
    onFile(file);
  }

  return (
    <div className={className}>
      <div className={cn(styles.wrap, file && styles.hasFile, draggingOver && styles.draggingOver)}>
        <div className={cn(styles.bg)}>
          <StateBasedFadeTransition
            state={String(!!file)}
            elementByStateMap={{
              true: (
                <span>
                  <FileLoadedIcon />
                </span>
              ),
              false: (
                <span>
                  <AddFileIcon />
                </span>
              ),
            }}
          />
          <StateBasedFadeTransition
            state={String(!!file)}
            elementByStateMap={{
              true: (
                <div>
                  <div className={styles.filename}>
                    <span className={styles.name}>{fileNameNoExtension}</span>
                    <span className={styles.extension}>.{fileExtension}</span>
                  </div>
                  {/* {fileName} */}
                  <div className={styles.filesize}>
                    {file?.type.includes('text/csv') ? 'CSV - ' : ''}
                    {formatBytes(file?.size, 1)}
                  </div>
                </div>
              ),
              false: <div className={styles.dragAndDropText}>{text}</div>,
            }}
          />
        </div>
        <button
          type="button"
          className={styles.dropArea}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={onUploadClick}
        ></button>
        <input
          type="file"
          ref={browseInputRef}
          className={styles.browse}
          onChange={onBrowseFileChange}
          accept={accept}
          id="import-file"
        />
      </div>
    </div>
  );
}
